import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import styled from 'styled-components';

import DefaultInput from 'components/input/DefaultInput';
import DefaultButton from 'components/button/DefaultButton';
import { QueryKeys, restfetcher, getClient } from 'queryClient';

import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import { useSetAtom } from 'jotai';
import modalInformationAtom from 'atom/modalInformationAtom';
import { useLocation } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useTimerFromUtc } from 'hooks/useTimerFromUtc';
import DefaultSelectMenu from 'components/selectMenu/DefaultSelectMenu';
import { EMAIL_LIST } from 'consts/join';

let timer: any = null;
let clickCount = 0;

interface Props {
  emailState: InputEmail;
  setStep1: React.Dispatch<React.SetStateAction<boolean>>;
  setEmailState: React.Dispatch<React.SetStateAction<InputEmail>>;
}

const basicEmailAddressStringKey = '직접입력';

const Step1 = ({ setStep1, emailState, setEmailState }: Props) => {
  const queryClient = getClient();

  const location = useLocation();
  const loginId = location?.state?.loginId;

  const codeInputRef = useRef<any>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [emailAuthIdx, setEmailAuthIdx] = useState(0);
  const [authLoadingState, setAuthLoadingState] = useState(false);
  const [status, setStatus] = useState<InputStateType>({
    status: undefined,
    text: ''
  });

  const { watch, setValue } = useFormContext<JoinTypeOnlyUsedFront>();

  const usdId = watch('usrid');

  const setModalInformation = useSetAtom(modalInformationAtom);

  const { timeLeft: countTime, startTimer, resetTimer } = useTimerFromUtc();

  const sendAuthEmail = useMutation({
    mutationFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/auth/send/authmail',
        params: {
          usrid: usdId
        }
      }),
    onSuccess: (data: any) => {
      if (data?.result === 'success') {
        clickCount = clickCount + 1;

        startTimer(data?.expireddate);
        setEmailAuthIdx(data?.emailauthidx);
        setModalInformation({
          type: 'SENDEMAIL_CODE_ALERT'
        });
      } else if (data?.result === 'failed' && data?.exception.includes('already join')) {
        setModalInformation({
          type: 'WARNING',
          message: '이미 가입된 이메일입니다.'
        });
      } else {
        setModalInformation({
          type: 'WARNING',
          message: '재시도 부탁드립니다.'
        });
      }
      setAuthLoadingState(false);
    }
  });

  const requestValidateAuthcode = useMutation({
    mutationFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/auth/authcode',
        params: {
          emailauthidx: emailAuthIdx,
          authcode: verificationCode
        }
      }),
    onSuccess: (data: any) => {
      if (data?.result === 'success') {
        setStatus({
          status: 'success',
          text: '일치합니다.'
        });
        setStep1(true);
      } else {
        setStatus({
          status: 'error',
          text: '일치하지 않습니다.'
        });
        setStep1(false);
      }
    }
  });

  const handleClickConfirmCode = () => {
    if (clickCount > 0) {
      if (countTime === null) {
        setModalInformation({
          type: 'SENDEMAIL_CODE_ERROR'
        });
        setVerificationCode('');
        codeInputRef.current.value = '';
        clickCount = 0;
      } else if (verificationCode.length > 0) {
        requestValidateAuthcode.mutate();
      }
    } else {
      setVerificationCode('');
      setModalInformation({
        type: 'NOSENDEMAIL_CODE'
      });
    }
  };

  const updateFinalEmail = (id: string | undefined, domain?: string | undefined) => {
    const trimmedId = id?.trim() || '';
    const trimmedDomain = domain?.trim() || '';

    if (trimmedId && trimmedDomain) {
      setValue('usrid', `${trimmedId}@${trimmedDomain}`);
    } else {
      setValue('usrid', trimmedId);
    }
  };

  const handleChangeEmailId = (value: string) => {
    const trimmedValue = value.trim();

    setEmailState((prev) => ({
      ...prev,
      emailId: trimmedValue,
      error: { text: '', type: '' }
    }));

    updateFinalEmail(trimmedValue, emailState.selectedEmailAddress || emailState.directInputEmail);
  };

  const handleDirectInputEmail = (value: string) => {
    const trimmedDomain = value.trim();

    setEmailState((prev) => ({
      ...prev,
      directInputEmail: trimmedDomain,
      selectedEmailAddress: '',
      error: { text: '', type: '' }
    }));

    updateFinalEmail(trimmedDomain);
  };

  const handleSelectEmailAddress = (value: string | number) => {
    if (typeof value === 'number') {
      return;
    }

    setEmailState((prev) => {
      const updatedEmailId = prev.emailId;
      updateFinalEmail(updatedEmailId, value);

      return {
        ...prev,
        selectedEmailAddress: value,
        directInputEmail: '',
        emailId: '',
        error: { text: '', type: '' }
      };
    });
  };

  const handleChangeVerificationCode = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setVerificationCode(value);
  };

  const handleSendEmail = () => {
    setModalInformation({
      type: 'SENDEMAIL_CODE_CONFIRM',
      handleClickRightBtn: () => {
        resetTimer();
        clearInterval(timer);
        sendAuthEmail.mutate();
        setAuthLoadingState(true);
      }
    });
  };

  useEffect(() => {
    if (loginId) {
      setEmailState((prev) => ({
        ...prev,
        emailId: '',
        selectedEmailAddress: '',
        directInputEmail: loginId || ''
      }));

      setValue('usrid', loginId);
    }
  }, [loginId]);

  useEffect(() => {
    setStatus({
      status: undefined,
      text: ''
    });
    setStep1(false);
    queryClient.removeQueries({ queryKey: [QueryKeys.AUTHCODE] });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode]);

  return (
    <Wrapper>
      <ContentsWrapper>
        <Contents>
          <LeftKey>이메일</LeftKey>
          <EmailWrapper>
            <RightValue>
              <div className="select">
                <DefaultInput
                  value={emailState.emailId}
                  name="emailId"
                  placeholder="이메일을 직접 입력하거나 선택해주세요."
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeEmailId(e.target.value);
                  }}
                  disabled={
                    emailState.selectedEmailAddress === basicEmailAddressStringKey ||
                    emailState.selectedEmailAddress === ''
                  }
                />
                <div>@</div>
                <DefaultSelectMenu
                  value={emailState.selectedEmailAddress || basicEmailAddressStringKey}
                  options={EMAIL_LIST.map((item) => ({
                    ...item,
                    label: item.value
                  }))}
                  size="large"
                  placeholder={basicEmailAddressStringKey}
                  onChange={(e) => handleSelectEmailAddress(e)}
                />
              </div>
              <DefaultInput
                value={emailState.directInputEmail}
                name="directInputEmail"
                placeholder="전체 이메일 주소를 입력해주세요."
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleDirectInputEmail(e.target.value);
                }}
                disabled={
                  !!emailState.selectedEmailAddress &&
                  emailState.selectedEmailAddress !== basicEmailAddressStringKey
                }
              />
            </RightValue>
            <DefaultButton
              text="인증코드 발송"
              onClick={handleSendEmail}
              size="middle"
              type="primary"
              loading={authLoadingState}
            />
          </EmailWrapper>
        </Contents>
      </ContentsWrapper>
      <Line />
      <InputWrapper>
        <LeftKey className="">인증코드*</LeftKey>
        <InputContents>
          <DefaultInput
            name="verificationCode"
            value={verificationCode}
            placeholder={
              countTime
                ? `${countTime?.minutes.toString().padStart(2, '0')}:${countTime?.seconds
                    .toString()
                    .padStart(2, '0')}`
                : '인증코드 발송을 진행해주세요.'
            }
            onChange={handleChangeVerificationCode}
            size="large"
            height="large"
            status={status.status}
            statusText={status.text}
            disabled={countTime === null && clickCount === 0}
            ref={codeInputRef}
          />
          <DefaultButton
            text="확인"
            size="small"
            type="primary"
            onClick={handleClickConfirmCode}
            disabled={verificationCode.length < 1}
          />
        </InputContents>
      </InputWrapper>
      <Line />
    </Wrapper>
  );
};

export default Step1;

const Wrapper = styled.div`
  color: ${color.black2};
`;

const ContentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export const LeftKey = styled.p`
  width: 160px;
  ${font.h3_Regular};
`;

export const RightValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & div.select {
    align-items: center;
  }

  & .ant-input-affix-wrapper {
    width: 360px;
  }

  & .ant-select.ant-select-outlined {
    width: 160px;
  }

  & > div {
    display: flex;
    gap: 8px;
  }

  ${font.h3_medium};
`;

const Contents = styled.div`
  display: flex;
  gap: 16px;
`;

const InputContents = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const EmailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const Line = styled.p`
  width: 100%;
  margin: 32px 0;
  background-color: ${color.opacityBlack01};
  height: 0.5px;
`;
